import { useCallback, useMemo } from "react"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import type { DirectoryEntry, ShareRecipient } from "../chatbox/resource-sharing"
import { ShareDialog, type ShareDialogWording, recipientAnalyticsFields } from "../chatbox/share-dialog"

export interface WorkflowShareDialogProps {
  open: boolean
  onClose: () => void
  scheduledWorkflowId: string
  workflowName: string
  resourceId: string
  source: WorkflowShareSource
}

export type WorkflowShareSource = "list" | "report"

const WORKFLOW_SHARE_WORDING: ShareDialogWording = {
  title: "Share workflow",
  accessNote: "Everyone you add can see this workflow's reports. They can't edit or re-share it.",
  shareFailure: "Couldn't share this workflow. Try again.",
}

export function WorkflowShareDialog({ open, onClose, scheduledWorkflowId, workflowName, resourceId, source }: WorkflowShareDialogProps) {
  const analytics = useAnalytics()

  const resolveResourceId = useCallback(async () => resourceId, [resourceId])

  const target = useMemo(() => ({
    resourceId,
    isLoading: false,
    isError: false,
    isCreating: false,
    resolveResourceId,
  }), [resourceId, resolveResourceId])

  const onShared = useCallback((_resourceId: string, granted: DirectoryEntry[]) => {
    analytics.track(AI_TEAMMATE_EVENTS.SCHEDULED_WORKFLOW_SHARED, {
      scheduledWorkflowId,
      source,
      ...recipientAnalyticsFields(granted),
    })
  }, [analytics, scheduledWorkflowId, source])

  const onUnshared = useCallback((_resourceId: string, recipient: ShareRecipient) => {
    analytics.track(AI_TEAMMATE_EVENTS.SCHEDULED_WORKFLOW_UNSHARED, {
      scheduledWorkflowId,
      source,
      recipientKind: recipient.kind,
      recipientId: recipient.id,
    })
  }, [analytics, scheduledWorkflowId, source])

  return (
    <ShareDialog
      open={open}
      onClose={onClose}
      resourceType="SCHEDULED_WORKFLOW"
      resourceName={workflowName}
      target={target}
      wording={WORKFLOW_SHARE_WORDING}
      idPrefix="workflow-share"
      onShared={onShared}
      onUnshared={onUnshared}
    />
  )
}
